"use client";

import { useState, useEffect, type FormEvent } from "react";
import Link from "next/link";
import type { EventbriteEventFormatted } from "@/lib/eventbrite";
import { EventbriteEventCard } from "./EventbriteEventCard";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "vault88-private-events-email";

type Props = {
  publicEvents: EventbriteEventFormatted[];
  pastPublicEvents: EventbriteEventFormatted[];
  hasPrivateEvents: boolean;
};

type Tab = "public" | "private";

type PublicView = "upcoming" | "past";

export function EventsClient({
  publicEvents,
  pastPublicEvents,
  hasPrivateEvents
}: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("public");
  const [publicView, setPublicView] = useState<PublicView>("upcoming");
  const [email, setEmail] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedEvents, setVerifiedEvents] = useState<EventbriteEventFormatted[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);

  // Restore email from localStorage on mount
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setEmail(stored);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setIsVerifying(true);
    setError(null);
    setVerifiedEvents(null);

    try {
      const res = await fetch("/api/events/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error ?? "Failed to verify email. Please try again.");
        return;
      }

      setVerifiedEvents(data.events ?? []);
      setVerifiedEmail(trimmed);

      // Persist email for convenience on return visits
      try {
        window.localStorage.setItem(STORAGE_KEY, trimmed);
      } catch {
        // Ignore
      }
    } catch {
      setError("Something went wrong. Please check your connection and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReset = () => {
    setVerifiedEvents(null);
    setVerifiedEmail(null);
    setError(null);
  };

  const showPrivateTab = hasPrivateEvents;

  return (
    <>
      {/* Tab bar — only shown when there are private events */}
      {showPrivateTab && (
        <div className="border-b border-slate-200 px-6 md:px-12">
          <div className="mx-auto max-w-6xl flex gap-0">
            <button
              onClick={() => setActiveTab("public")}
              className={cn(
                "px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] transition-colors border-b-2 -mb-[1px]",
                activeTab === "public"
                  ? "border-[var(--primary)] text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              )}
            >
              Public Events
            </button>
            <button
              onClick={() => setActiveTab("private")}
              className={cn(
                "px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] transition-colors border-b-2 -mb-[1px]",
                activeTab === "private"
                  ? "border-[var(--primary)] text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              )}
            >
              Private Showings
            </button>
          </div>
        </div>
      )}

      {/* ── Public Events tab ──────────────────────────────────────────────── */}
      {activeTab === "public" && (
        <section className="px-6 py-16 md:px-12">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-[0.2em] text-slate-900 mb-3 font-[var(--font-display)]">
                  {publicView === "upcoming" ? "Public Events & Workshops" : "Past Events"}
                </h2>
                <p className="text-sm text-slate-600">
                  {publicView === "upcoming"
                    ? "Browse our upcoming events and workshops. Click through to register on Eventbrite."
                    : "A look at events we've hosted recently."}
                </p>
              </div>

              {pastPublicEvents.length > 0 && (
                <div className="inline-flex border border-slate-200 bg-white p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setPublicView("upcoming")}
                    className={cn(
                      "px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors",
                      publicView === "upcoming"
                        ? "bg-[var(--primary)] text-white"
                        : "text-slate-500 hover:text-slate-900"
                    )}
                  >
                    Upcoming
                  </button>
                  <button
                    type="button"
                    onClick={() => setPublicView("past")}
                    className={cn(
                      "px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors",
                      publicView === "past"
                        ? "bg-[var(--primary)] text-white"
                        : "text-slate-500 hover:text-slate-900"
                    )}
                  >
                    Past
                  </button>
                </div>
              )}
            </div>

            {(publicView === "upcoming" ? publicEvents : pastPublicEvents).length === 0 ? (
              <div className="glass my-10 mx-auto max-w-lg space-y-4 rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-900 shadow-soft">
                <p className="text-slate-600">
                  {publicView === "upcoming"
                    ? "No public events scheduled at this time."
                    : "No past events to display."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(publicView === "upcoming" ? publicEvents : pastPublicEvents).map((event) => (
                  <EventbriteEventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Private Showings tab ───────────────────────────────────────────── */}
      {activeTab === "private" && (
        <section className="px-6 py-16 md:px-12 bg-slate-50">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-[0.2em] text-slate-900 mb-3 font-[var(--font-display)]">
                Private Events & Showings
              </h2>
              <p className="text-sm text-slate-600">
                Exclusive invitations and private appointments for our valued
                clients.
              </p>
            </div>

            {/* Email verification gate */}
            {verifiedEvents === null ? (
              <div className="mx-auto max-w-lg">
                <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-soft">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Verify your invitation
                  </h3>
                  <p className="text-sm text-slate-600 mb-6">
                    Enter the email address associated with your invitation to
                    view your private events.
                  </p>

                  <form onSubmit={handleVerify} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                    <button
                      type="submit"
                      disabled={isVerifying || !email.trim()}
                      className={cn(
                        "w-full bg-[var(--primary)] text-white px-6 py-3 rounded-lg text-xs font-semibold uppercase tracking-[0.3em] transition",
                        isVerifying || !email.trim()
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-90"
                      )}
                    >
                      {isVerifying ? "Verifying..." : "Verify Email"}
                    </button>
                  </form>

                  {error && (
                    <p className="mt-4 text-sm text-red-600">{error}</p>
                  )}
                </div>
              </div>
            ) : verifiedEvents.length === 0 ? (
              <div className="mx-auto max-w-lg">
                <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-soft text-center">
                  <p className="text-slate-600 mb-2">
                    No private events found for{" "}
                    <span className="font-semibold text-slate-900">
                      {verifiedEmail}
                    </span>
                    .
                  </p>
                  <p className="text-sm text-slate-500 mb-6">
                    If you believe this is an error, please{" "}
                    <Link
                      href="/contact"
                      className="text-[var(--primary)] underline underline-offset-2 hover:opacity-80"
                    >
                      contact us
                    </Link>
                    .
                  </p>
                  <button
                    onClick={handleReset}
                    className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition"
                  >
                    Try a different email
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-sm text-slate-600">
                    Showing events for{" "}
                    <span className="font-semibold text-slate-900">
                      {verifiedEmail}
                    </span>
                  </p>
                  <button
                    onClick={handleReset}
                    className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition"
                  >
                    Change email
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {verifiedEvents.map((event) => (
                    <EventbriteEventCard key={event.id} event={event} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
